import React, { useEffect, useState } from "react";

import { useTranslation } from 'react-i18next';
import { Grid, Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";

import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { alertsActions } from "../../../../redux/alerts-slice";
import { selectAccountSettings } from "../../../../redux/store";

import { MegaphoneIcon } from "../../../../assets/icons";
import MassiveSendingList from "../MassiveSendingsList";
import { useStyles } from "./css";
import MassiveSendingModal from "../MassiveSendingModal";
import { MassiveSendingEntity } from "../../../../models/massive-sending-entity";
import { EditableItemProps } from "../../../../models/interfaces/editable-item-props";
import { MassiveSendingService } from "../../../../api/massive-sending-service";
import { ApiClient } from "../../../../api/api-client";
import ButtonWithSpinner from "../../../common/ButtonWithSpinner";
import { ButtonPropsBorderRadius } from "../../../../models/enums/button-props-border-radius";
import MassiveSendingServiceModel from "../../../../models/massive-sending-service-model";
import MassiveSendingDetail from "../MassiveSendingDetail";
import { ReportsService } from "../../../../api/reports-service";


const MassiveSengingsMain = (props: any) => {

    const { t } = useTranslation(["massivesendings"]);
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const hasMassiveSendings = ApiClient.getUserInfo().hasMassiveSendings;

    const { enqueueAlert } = alertsActions;
        
    const [massiveSending, setMassiveSending] = useState<MassiveSendingEntity>(new MassiveSendingEntity());
    const [oldMassiveSending, setOldMassiveSending] = useState<MassiveSendingEntity>(new MassiveSendingEntity());
    const [services, setServices] = useState<MassiveSendingServiceModel[]>([]);
    const [modalMode, setModalMode] = useState<EditableItemProps>({ mode: "new", itemId: null });
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [modalDetailOpen, setModalDetailOpen] = useState<boolean>(false);
    const [isRefresh, setRefresh] = useState<any>();
    const [total, setTotal] = useState<number>(0);
    const [existingMassiveSendingsNames, setExistingMassiveSendingsNames] = useState<string[]>([]);

    const accountSettings = useAppSelector(selectAccountSettings);

    async function handleListRowClick(itemId: string) {
        await handleOpenModal(itemId);
    }
    
    const handleConfirmDelete = async (item: any) => {
        const result = await MassiveSendingService.deleteSending(item.id);
        if (result && result.isDeleted) {
            setRefresh(result)
        }
    }

    const handleTotal = (total: number) => {
        setTotal(total);
    }

    async function handleOpenModal(itemId?: string | null) {
        const services = await MassiveSendingService.getServicesByAccount();
        setServices(services);

        const names = await MassiveSendingService.getSendingsNamesByAccount();
        setExistingMassiveSendingsNames(names);

        if (itemId) {
            const ms = await MassiveSendingService.getSendingById(itemId)
            setMassiveSending(ms);
            setOldMassiveSending(ms);
            setModalMode({ mode: "edit", itemId });
        } else {
            setModalMode({ mode: "new", itemId: null });
        }
        
        setModalOpen(true);
    }
    
    function displaySuccessfulCreationAlert(massiveSendingName: string) {
        dispatch(
          enqueueAlert({
              type: "Success",
              title: t("Massive sending successfully created"),
              description: `${massiveSendingName}`
          })
        )
    }
    
    function displaySuccessfulEditionAlert(massiveSendingName: string) {
        dispatch(
          enqueueAlert({
              type: "Success",
              title: t("Massive sending modified"),
              description: `${massiveSendingName}`
          })
        )
    }
    
    async function createNewSending(massiveSending: MassiveSendingEntity) {
        return massiveSending.image
          ? await MassiveSendingService.createSendingWithImage(massiveSending)
            .then(() => displaySuccessfulCreationAlert(massiveSending.name))
            .catch((e) => {
                console.log(e)
            })
          : await MassiveSendingService.createSending(massiveSending)
            .then(() => displaySuccessfulCreationAlert(massiveSending.name))
            .catch((e) => {
                console.log(e)
            });
    }
    
    async function updateSending(massiveSending: MassiveSendingEntity) {
        return massiveSending.image 
          ? await MassiveSendingService.updateSendingWithImage(massiveSending)
            .then(() => displaySuccessfulEditionAlert(massiveSending.name))
            .catch((e) => { console.log(e)})
          : await MassiveSendingService.updateSending(massiveSending)
            .then(() => displaySuccessfulEditionAlert(massiveSending.name))
            .catch((e) => { console.log(e)})
    }
    
    async function handleCompleteModal(massiveSending: MassiveSendingEntity) {
        const response = modalMode.mode === "new"
          ? await createNewSending(massiveSending)
          : await updateSending(massiveSending)

        setRefresh({ massiveSending });
    }
    
    async function handleCloseModal() {
        setModalMode({ mode: "new", itemId: null });
        setModalOpen(false);
    }

    async function handleModalDetailOpen(massiveSending: MassiveSendingEntity) {
      if (!massiveSending.id) return;

      const detailedSending = await MassiveSendingService.getSendingById(massiveSending.id);
      setMassiveSending(detailedSending);

      setModalDetailOpen(true);
    }

    async function handleMassiveSendingDuplicate(massiveSending: MassiveSendingEntity) {
      setModalDetailOpen(false);

      const services = await MassiveSendingService.getServicesByAccount();
      setServices(services);

      const names = await MassiveSendingService.getSendingsNamesByAccount();
      setExistingMassiveSendingsNames(names);

      setModalMode({ mode: "new", itemId: massiveSending.id });

      setModalOpen(true);
    }

    async function handleReportDownload(massiveSendingId: string) {
      const newDate = new Date();
      const year = newDate.getFullYear();
      const month = newDate.getMonth() + 1;
      const today = newDate.getDate();

      const fileName = `${accountSettings.accountName} - ${t('Impacted customers report')} - ${today}-${month}-${year}.xlsx`;

      const blob = await ReportsService.getImpactedCustomersReport(massiveSendingId);

      const url = window.URL.createObjectURL(new Blob([blob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"
      }));

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    function handleModalDetailClose() {
      setModalDetailOpen(false);
      setModalMode({ mode: "new", itemId: null }); 
      setMassiveSending(new MassiveSendingEntity());
    }

    useEffect(() => {
        if (props.match.params?.sendingId) {
        handleOpenModal(props.match.params?.sendingId)
        }
    }, [])


    return (<>
        <div>
            <div className={classes.header}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <div>
                            <Typography className={classes.titles} variant="subtitle1">
                                {t('Massive sendings lists')}
                            </Typography>
                            <Typography className={classes.icons} variant="h4">
                                <MegaphoneIcon className={classes.icon} />
                                {total}
                            </Typography>
                        </div>
                    </Grid>
                    <Grid item xs={6}>
                    {
                        hasMassiveSendings && 
                        <div className={classes.headerRight}>
                        <ButtonWithSpinner
                          borderRadius={ButtonPropsBorderRadius.Large}
                          innerText={t('New massive sending')}
                          onClick={() => handleOpenModal()}
                        />
                        </div>
                    }
                    </Grid>
                </Grid>
            </div>
            
            <MassiveSendingList 
                isRefresh={isRefresh} 
                onRowClick={handleListRowClick} 
                onDelete={handleConfirmDelete}
                handleModalDetailOpen={handleModalDetailOpen}
                setTotalRecords={handleTotal} 
            />

            <MassiveSendingModal 
                massiveSending={massiveSending} 
                services={services}
                setMassiveSending={setMassiveSending} 
                oldMassiveSending={oldMassiveSending}
                mode={modalMode.mode}
                itemId={modalMode.itemId}
                open={modalOpen}
                setOpen={setModalOpen}
                onCreate={handleCompleteModal}
                onClose={handleCloseModal}
                existingMassiveSendingsNames={existingMassiveSendingsNames}
            />

            <MassiveSendingDetail 
              massiveSending={massiveSending}
              open={modalDetailOpen}
              setOpen={setModalDetailOpen}
              onClose={handleModalDetailClose}
              onReportDownload={handleReportDownload}
              onDuplicate={handleMassiveSendingDuplicate}
            />
            
        </div>
    </>);
}

export default MassiveSengingsMain;